import { HeartHandshake, ArrowUpRight, Shield } from "lucide-react";

// Live Paystack donation link — replaces mailto placeholder.
export const DONATION_URL = "https://paystack.shop/pay/55y140q79p";

const TIERS = [
  {
    id: "materials",
    amount: "$10",
    label: "Support Materials",
    sub: "Fibre · beads · pigment",
  },
  {
    id: "tools",
    amount: "$25",
    label: "Support Tools",
    sub: "Chisels · needles · looms",
  },
  {
    id: "craftsmanship",
    amount: "$50",
    label: "Support Craftsmanship",
    sub: "A full week of a maker’s work",
  },
];

export default function Donation() {
  return (
    <section
      id="support"
      data-testid="donation-section"
      className="relative py-24 md:py-36 px-6 md:px-12 lg:px-20 bg-[#0B0B0B] overflow-hidden"
    >
      {/* Subtle ornamental dots */}
      <div className="absolute inset-0 ornament opacity-[0.18] pointer-events-none" />

      {/* Thin gold frame */}
      <div className="relative max-w-6xl mx-auto">
        <div className="relative border border-[#D4AF37]/25 p-8 sm:p-12 md:p-16 lg:p-20 reveal">
          {/* Corner ornaments */}
          <span className="absolute top-0 left-0 w-px h-8 bg-[#D4AF37]" />
          <span className="absolute top-0 left-0 h-px w-8 bg-[#D4AF37]" />
          <span className="absolute top-0 right-0 w-px h-8 bg-[#D4AF37]" />
          <span className="absolute top-0 right-0 h-px w-8 bg-[#D4AF37]" />
          <span className="absolute bottom-0 left-0 w-px h-8 bg-[#D4AF37]" />
          <span className="absolute bottom-0 left-0 h-px w-8 bg-[#D4AF37]" />
          <span className="absolute bottom-0 right-0 w-px h-8 bg-[#D4AF37]" />
          <span className="absolute bottom-0 right-0 h-px w-8 bg-[#D4AF37]" />

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            {/* Left — icon + overline */}
            <div className="lg:col-span-4">
              <div className="w-14 h-14 border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37] mb-7">
                <HeartHandshake className="w-6 h-6" strokeWidth={1.4} />
              </div>
              <span
                data-testid="donation-overline"
                className="text-[11px] tracking-[0.42em] uppercase text-[#D4AF37]"
              >
                Preserve the craft
              </span>
              <div className="mt-6 hidden lg:block">
                <div className="gold-divider w-16" />
                <p className="mt-8 text-[11px] tracking-[0.32em] uppercase text-white/45 leading-loose">
                  For the artisans <br />
                  who keep tradition <br />
                  alive.
                </p>
              </div>
            </div>

            {/* Right — headline, copy, tiers, CTA */}
            <div className="lg:col-span-8">
              <h2
                data-testid="donation-headline"
                className="font-serif-display text-[30px] sm:text-4xl md:text-5xl lg:text-[52px] leading-[1.1] tracking-tight text-white"
              >
                Every thread. Every carving. <br />
                <span className="italic text-gold-shimmer">
                  A story worth preserving.
                </span>
              </h2>

              <div className="mt-8 space-y-5 max-w-2xl">
                <p className="text-[15px] md:text-base leading-[1.9] text-white/75 font-light">
                  Behind every woven basket and hand-carved piece is a story of
                  resilience, culture, and craftsmanship.
                </p>
                <p className="text-[15px] md:text-base leading-[1.9] text-white/75 font-light">
                  Your contribution directly supports artisans, sustains
                  livelihoods, and keeps generations of tradition alive.
                </p>
              </div>

              {/* Suggested tiers */}
              <div
                data-testid="donation-tiers"
                className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4"
              >
                {TIERS.map((t) => (
                  <a
                    key={t.id}
                    href={DONATION_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() =>
                      typeof window !== "undefined" &&
                      window.trackDonation &&
                      window.trackDonation("paystack", { tier: t.id, amount: t.amount })
                    }
                    data-testid={`donation-tier-${t.id}`}
                    className="group block border border-[#262626] hover:border-[#D4AF37]/60 bg-[#0e0e0e] hover:bg-[#121212] p-5 md:p-6 transition-all duration-400"
                  >
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="font-serif-display text-2xl md:text-3xl text-gold-shimmer">
                        {t.amount}
                      </span>
                      <ArrowUpRight className="w-4 h-4 text-white/30 group-hover:text-[#D4AF37] transition-colors" />
                    </div>
                    <p className="mt-4 text-[11px] tracking-[0.28em] uppercase text-white/80 group-hover:text-[#D4AF37] transition-colors">
                      {t.label}
                    </p>
                    <p className="mt-2 text-[11px] text-white/45 font-light">
                      {t.sub}
                    </p>
                  </a>
                ))}
              </div>

              {/* Primary CTA */}
              <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-5">
                <a
                  href={DONATION_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    typeof window !== "undefined" &&
                    window.trackDonation &&
                    window.trackDonation("paystack", { tier: "primary_cta" })
                  }
                  data-testid="donation-support-btn"
                  className="btn-gold"
                >
                  <span>Support the Artisans</span>
                  <ArrowUpRight className="w-4 h-4" />
                </a>
                <span className="text-[10px] tracking-[0.32em] uppercase text-white/40">
                  100% goes to artisan communities
                </span>
              </div>

              {/* Paystack trust line */}
              <div
                data-testid="donation-paystack-trust"
                className="mt-6 inline-flex items-center gap-2 text-[10px] tracking-[0.32em] uppercase text-white/50"
              >
                <Shield className="w-3.5 h-3.5 text-[#D4AF37]" />
                Secure payments powered by Paystack
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
